import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  Users,
  Building2,
  DollarSign,
  Award,
  ArrowLeft,
  Calendar,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import { useTheme } from '../context/ThemeContext';
import StyledDropdown from './StyledDropdown';

const AdminAnalytics = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);
  const [groupPerformance, setGroupPerformance] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [topPerGroup, setTopPerGroup] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);

  // Chart colors based on theme
  const axisColor = isDarkMode ? '#94a3b8' : '#6b7280';
  const gridColor = isDarkMode ? '#334155' : '#f0f0f0';

  // Filters
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => currentYear + 1 - i);
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `RM ${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `RM ${(amount / 1000).toFixed(1)}K`;
    }
    return `RM ${amount.toFixed(2)}`;
  };

  const formatCurrencyFull = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount);
  };

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [overviewRes, trendRes, groupRes, topRes, topGroupRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/analytics/admin/overview?year=${filterYear}`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/analytics/admin/sales-trend?year=${filterYear}`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/analytics/admin/group-performance?month=${filterMonth}&year=${filterYear}`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/analytics/admin/top-performers?month=${filterMonth}&year=${filterYear}&limit=10`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/analytics/admin/top-per-group?month=${filterMonth}&year=${filterYear}`, { headers: getAuthHeaders() }),
      ]);

      const [overviewData, trendData, groupData, topData, topGroupData] = await Promise.all([
        overviewRes.json(),
        trendRes.json(),
        groupRes.json(),
        topRes.json(),
        topGroupRes.json(),
      ]);

      if (overviewData.success) setOverview(overviewData.data);
      if (trendData.success) setSalesTrend(trendData.data.monthly);
      if (groupData.success) setGroupPerformance(groupData.data.groups);
      if (topData.success) setTopPerformers(topData.data.performers);
      if (topGroupData.success) setTopPerGroup(topGroupData.data.groups);

    } catch (err) {
      console.error('Fetch analytics error:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch group detail
  const fetchGroupDetail = async (groupId) => {
    try {
      const res = await fetch(
        `${getApiUrl()}/api/analytics/admin/group/${groupId}?month=${filterMonth}&year=${filterYear}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.success) {
        setGroupDetail(data.data);
        setSelectedGroup(groupId);
      }
    } catch (err) {
      console.error('Fetch group detail error:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterYear, filterMonth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Group Detail View
  if (selectedGroup && groupDetail) {
    return (
      <div className="p-6">
        {/* Back Button */}
        <button
          onClick={() => {
            setSelectedGroup(null);
            setGroupDetail(null);
          }}
          className="flex items-center gap-2 text-gray-600 dark:text-dark-muted hover:text-gray-800 dark:hover:text-dark-text mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Overview
        </button>

        {/* Group Header */}
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">{groupDetail.group.name}</h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">
            Group Leader: {groupDetail.group.groupLeader} | 
            {months.find(m => m.value === filterMonth)?.label} {filterYear}
          </p>
        </div>

        {/* Group Monthly Trend */}
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6 transition-colors duration-200">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-4">Monthly Sales Trend</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={groupDetail.monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis 
                  dataKey="monthName" 
                  tick={{ fontSize: 12, fill: axisColor }} 
                  stroke={axisColor}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: axisColor }} 
                  tickFormatter={(value) => formatCurrency(value)}
                  stroke={axisColor}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrencyFull(value), 'Sales']}
                  labelStyle={{ fontWeight: 'bold' }}
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                    border: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`,
                    color: isDarkMode ? '#e2e8f0' : '#111827'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="sales" 
                  stroke="#00837e" 
                  strokeWidth={2}
                  dot={{ fill: '#00837e', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Member Leaderboard */}
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden transition-colors duration-200">
          <div className="p-4 border-b border-gray-200 dark:border-dark-border">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text">Member Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Role</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Sales Count</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Total Sales</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                {groupDetail.members.map((member) => (
                  <tr key={member.userId} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                        member.rank === 1 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                        member.rank === 2 ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                        member.rank === 3 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                        'bg-gray-50 dark:bg-slate-800/50 text-gray-600 dark:text-dark-muted'
                      }`}>
                        {member.rank}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-dark-text">
                      {member.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-dark-muted">
                      {typeof member.role === 'string' ? member.role : member.role?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600 dark:text-dark-muted">
                      {member.salesCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-gray-900 dark:text-dark-text">
                      {formatCurrencyFull(member.totalSales)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Main Overview
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Analytics Dashboard</h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">Overview of sales performance</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <StyledDropdown
            options={months.map(m => ({ value: m.value, label: m.label }))}
            value={filterMonth}
            onChange={(val) => setFilterMonth(parseInt(val))}
            placeholder="Select Month"
            icon={Calendar}
            className="min-w-[150px]"
          />
          <StyledDropdown
            options={years.map(y => ({ value: y, label: y.toString() }))}
            value={filterYear}
            onChange={(val) => setFilterYear(parseInt(val))}
            placeholder="Select Year"
            className="min-w-[120px]"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <DollarSign size={24} className="text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">Sales (This Month)</p>
                <p className="text-xl font-bold text-gray-900 dark:text-dark-text">{formatCurrency(overview.currentMonth.sales)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <TrendingUp size={24} className="text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">Sales (YTD)</p>
                <p className="text-xl font-bold text-gray-900 dark:text-dark-text">{formatCurrency(overview.ytd.sales)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Users size={24} className="text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">Active Users</p>
                <p className="text-xl font-bold text-gray-900 dark:text-dark-text">{overview.users.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Building2 size={24} className="text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">Active Groups</p>
                <p className="text-xl font-bold text-gray-900 dark:text-dark-text">{overview.groups}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Trend Chart */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6 transition-colors duration-200">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-4">Monthly Sales Trend ({filterYear})</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={salesTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis 
                dataKey="monthName" 
                tick={{ fontSize: 12, fill: axisColor }} 
                stroke={axisColor}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: axisColor }} 
                tickFormatter={(value) => formatCurrency(value)}
                stroke={axisColor}
              />
              <Tooltip 
                formatter={(value) => [formatCurrencyFull(value), 'Sales']}
                labelStyle={{ fontWeight: 'bold' }}
                contentStyle={{ 
                  backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                  border: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`,
                  color: isDarkMode ? '#e2e8f0' : '#111827'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="sales" 
                stroke="#00837e" 
                strokeWidth={2}
                dot={{ fill: '#00837e', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Group Performance & Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Group Performance Bar Chart */}
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6 transition-colors duration-200">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-4">Group Performance</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={groupPerformance} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis 
                  type="number" 
                  tickFormatter={(value) => formatCurrency(value)} 
                  tick={{ fontSize: 11, fill: axisColor }} 
                  stroke={axisColor}
                />
                <YAxis 
                  dataKey="groupName" 
                  type="category" 
                  tick={{ fontSize: 11, fill: axisColor }} 
                  width={100}
                  stroke={axisColor}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrencyFull(value), 'Sales']}
                  labelStyle={{ fontWeight: 'bold' }}
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                    border: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`,
                    color: isDarkMode ? '#e2e8f0' : '#111827'
                  }}
                />
                <Bar 
                  dataKey="totalSales" 
                  fill="#00837e" 
                  radius={[0, 4, 4, 0]}
                  cursor="pointer"
                  onClick={(data) => fetchGroupDetail(data.groupId)}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-gray-500 dark:text-dark-muted mt-2 text-center">Click on a bar to see group details</p>
        </div>

        {/* Top 10 Performers */}
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6 transition-colors duration-200">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-4">
            <Award className="inline-block mr-2 text-yellow-500" size={20} />
            Top 10 Performers
          </h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {topPerformers.map((performer, index) => (
              <div 
                key={performer.userId} 
                className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                    index === 0 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                    index === 1 ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                    index === 2 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                    'bg-gray-50 dark:bg-slate-800/50 text-gray-600 dark:text-dark-muted'
                  }`}>
                    {index + 1}
                  </span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-dark-text">{performer.name}</p>
                    <p className="text-xs text-gray-500 dark:text-dark-muted">{typeof performer.role === 'string' ? performer.role : performer.role?.name || 'N/A'} • {performer.group}</p>
                  </div>
                </div>
                <span className="font-semibold text-gray-900 dark:text-dark-text">{formatCurrency(performer.totalSales)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Performer from Each Group */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6 transition-colors duration-200">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-4">Top Performer from Each Group</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {topPerGroup.map((group) => (
            <div 
              key={group.groupId} 
              className="border border-gray-200 dark:border-dark-border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer bg-white dark:bg-slate-800/50"
              onClick={() => fetchGroupDetail(group.groupId)}
            >
              <h3 className="font-semibold text-gray-800 dark:text-dark-text mb-2">{group.groupName}</h3>
              {group.topPerformer ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <Award size={16} className="text-yellow-500" />
                    <span className="text-gray-900 dark:text-dark-text">{group.topPerformer.name}</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-dark-muted mb-2">{typeof group.topPerformer.role === 'string' ? group.topPerformer.role : group.topPerformer.role?.name || 'N/A'}</p>
                  <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                    {formatCurrency(group.topPerformer.totalSales)}
                  </p>
                </>
              ) : (
                <p className="text-gray-400 dark:text-dark-muted italic">No sales this period</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;








